/**
 * Phase 23 — Itinerary Map View (web).
 *
 * A TOGGLED inline map (not a side-by-side pane): the itinerary drawn
 * as a route — numbered day markers, POI dots with category emoji,
 * and per-segment line styles by travel mode (dashed = flight,
 * solid = road, long-dash = rail, dotted = walk/ferry).
 *
 * Cost design: Leaflet (open-source, no API key) is injected from CDN
 * ONLY when the user first toggles the map, and tiles come from the
 * free CARTO/OSM basemap — so the default card ships zero extra bytes
 * and non-map users cost nothing. Swap TILE_URL to self-hosted
 * Protomaps/PMTiles later for fully vendor-free scale.
 */
import { useEffect, useRef, useState } from "react";
import { Map as MapIcon, X } from "lucide-react";

const LEAFLET_JS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js";
const LEAFLET_CSS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css";
const TILE_URL = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const TILE_ATTR =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const CATEGORY_EMOJI = {
    cultural: "🏛", heritage: "🏛", spiritual: "🕉", food: "🍜",
    nature: "🌿", hills: "⛰", adventure: "🧗", nightlife: "🌙",
    shopping: "🛍", beach: "🌊", island: "🌴", luxury: "👑", city: "🏙",
};

function loadLeaflet() {
    return new Promise((resolve, reject) => {
        if (window.L) return resolve(window.L);
        if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
            const css = document.createElement("link");
            css.rel = "stylesheet";
            css.href = LEAFLET_CSS;
            document.head.appendChild(css);
        }
        const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve(window.L));
            existing.addEventListener("error", reject);
            return;
        }
        const js = document.createElement("script");
        js.src = LEAFLET_JS;
        js.async = true;
        js.onload = () => resolve(window.L);
        js.onerror = reject;
        document.head.appendChild(js);
    });
}

/** Segment line style from the NEXT day's transit text. */
function segmentStyle(transitText) {
    const t = String(transitText || "").toLowerCase();
    if (/\bfly|flight|air\b/.test(t)) return { dashArray: "6 8", weight: 2 };          // flight
    if (/\btrain|rail|shinkansen|metro\b/.test(t)) return { dashArray: "12 6", weight: 2.5 }; // rail
    if (/\bwalk|stroll|ferry|boat\b/.test(t)) return { dashArray: "2 6", weight: 2 };  // walk/ferry
    return { dashArray: null, weight: 2.5 };                                            // road (default)
}

export default function ItineraryMapView({ card, onClose }) {
    const holderRef = useRef(null);
    const mapRef = useRef(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let disposed = false;
        (async () => {
            try {
                const L = await loadLeaflet();
                if (disposed || !holderRef.current) return;
                const days = (card?.days || []).filter(
                    (d) => d?.city_coords && Number.isFinite(d.city_coords.lat),
                );
                if (days.length === 0) { setErr("No mappable locations in this plan yet."); return; }

                const map = L.map(holderRef.current, {
                    zoomControl: true, attributionControl: true, scrollWheelZoom: false,
                });
                mapRef.current = map;
                L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 18 }).addTo(map);

                const pts = [];
                let prev = null;
                days.forEach((d, i) => {
                    const p = [d.city_coords.lat, d.city_coords.lng];
                    pts.push(p);
                    // Route segment from previous day, styled by travel mode.
                    if (prev && (prev[0] !== p[0] || prev[1] !== p[1])) {
                        const transit = (d.blocks || []).map((b) => b?.transit_from_previous).find(Boolean);
                        const st = segmentStyle(transit);
                        L.polyline([prev, p], {
                            color: "#0A0A0A", opacity: 0.75,
                            weight: st.weight, dashArray: st.dashArray || undefined,
                        }).addTo(map);
                    }
                    prev = p;
                    // Numbered day marker.
                    L.marker(p, {
                        icon: L.divIcon({
                            className: "",
                            html: `<div style="width:26px;height:26px;border-radius:13px;background:#0A0A0A;color:#fff;display:flex;align-items:center;justify-content:center;font:600 12px Inter,system-ui;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)">${d.day || i + 1}</div>`,
                            iconSize: [26, 26], iconAnchor: [13, 13],
                        }),
                    }).addTo(map).bindPopup(
                        `<b>Day ${d.day || i + 1} · ${d.city || ""}</b>${d.title ? `<br/>${d.title}` : ""}`,
                    );
                    // POI dots for blocks that carry approximate coords.
                    (d.blocks || []).forEach((b) => {
                        if (!Number.isFinite(b?.lat) || !Number.isFinite(b?.lng)) return;
                        const emoji = CATEGORY_EMOJI[String(b.category || "").toLowerCase()] || "📍";
                        L.marker([b.lat, b.lng], {
                            icon: L.divIcon({
                                className: "",
                                html: `<div style="font-size:15px;line-height:15px;filter:drop-shadow(0 1px 1px rgba(0,0,0,.4))">${emoji}</div>`,
                                iconSize: [16, 16], iconAnchor: [8, 8],
                            }),
                        }).addTo(map).bindPopup(
                            `${emoji} ${String(b.activity || "").slice(0, 80)}`,
                        );
                    });
                });
                map.fitBounds(L.latLngBounds(pts).pad(0.25));
            } catch (e) {
                if (!disposed) setErr("Map failed to load — check your connection.");
            }
        })();
        return () => {
            disposed = true;
            if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
        };
    }, [card]);

    return (
        <div className="mt-3 overflow-hidden rounded-xl border border-[#E5E5E5]" data-testid="itinerary-map-view">
            <div className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.14em] text-[#525252]">
                    <MapIcon size={12} /> ROUTE MAP
                    <span className="ml-2 font-normal normal-case tracking-normal text-[#A3A3A3]">
                        — — flight&nbsp;&nbsp;· · walk&nbsp;&nbsp;— rail/road
                    </span>
                </span>
                <button type="button" onClick={onClose} aria-label="Close map"
                        data-testid="itinerary-map-close"
                        className="rounded p-1 text-[#737373] transition hover:bg-[#EFEFEF]">
                    <X size={14} />
                </button>
            </div>
            {err ? (
                <div className="px-3 py-6 text-center text-[12px] text-[#737373]">{err}</div>
            ) : (
                <div ref={holderRef} style={{ height: 340 }} />
            )}
        </div>
    );
}
